import { useEffect, useRef, useState } from "react";
import { submitForm } from "@/lib/api";

export default function AcademyApplyForm() {
  const successRef = useRef<HTMLDivElement>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    if (submitted) successRef.current?.focus();
  }, [submitted]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    setSubmitting(true);
    setError("");
    try {
      await submitForm("academy", form);
      setSubmitted(true);
      form.reset();
      setCharCount(0);
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : "Unable to submit your interest right now.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="apply" className="py-24 px-6 bg-white">
      <div className="max-w-[1280px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
        {/* Left - Info */}
        <div>
          <span className="inline-flex items-center gap-2 text-[#1A4D3C] text-xs font-semibold uppercase tracking-[0.25em] mb-5">
            <span className="w-8 h-px bg-[#C9A961]" />
            Register Your Interest
          </span>
          <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-bold text-[#2B2D2F] leading-tight mb-6">
            Register your interest.
          </h2>
          <p className="text-[#6B6B6B] text-base leading-relaxed mb-8">
            Take the first step toward building a disciplined, structured
            foundation in futures trading and financial literacy. Register your
            interest below. We will confirm program details before enrollment;
            submitting this form does not reserve a place or require payment.
          </p>

          <div className="form-intro-details space-y-5 mb-10">
            {[
              {
                icon: "ri-time-line",
                title: "Interest Registration",
                desc: "Takes less than 10 minutes to complete",
              },
              {
                icon: "ri-user-heart-line",
                title: "Personalized Guidance",
                desc: "Our team will reach out to discuss the right pathway for you",
              },
              {
                icon: "ri-lock-line",
                title: "No Commitment Required",
                desc: "Registering interest is the first step &mdash; no obligation to enroll",
              },
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A4D3C]/10 flex-shrink-0">
                  <i className={`${item.icon} text-[#1A4D3C] text-base`} />
                </div>
                <div>
                  <div className="font-semibold text-[#2B2D2F] text-sm mb-0.5">
                    {item.title}
                  </div>
                  <div
                    className="text-[#6B6B6B] text-xs"
                    dangerouslySetInnerHTML={{ __html: item.desc }}
                  />
                </div>
              </div>
            ))}
          </div>

          <img
            loading="lazy"
            src="/assets/images/site/acad-form-001-600x380.jpg"
            alt="Harambee Trading Academy application futures trading education"
            className="form-intro-photo w-full h-[260px] object-cover object-top rounded-lg"
          />
        </div>

        {/* Right - Form */}
        <div className="bg-[#F8F6F3] rounded-lg p-8 md:p-10 border border-[#E8E4DC]">
          {submitted ? (
            <div
              className="text-center py-12"
              role="status"
              tabIndex={-1}
              ref={successRef}
            >
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#1A4D3C]/10 mx-auto mb-5">
                <i className="ri-checkbox-circle-line text-[#1A4D3C] text-3xl" />
              </div>
              <h3 className="font-['Cormorant_Garamond'] text-3xl font-bold text-[#2B2D2F] mb-3">
                Interest Registered
              </h3>
              <p className="text-[#6B6B6B] text-sm leading-relaxed max-w-sm mx-auto">
                Thank you for your interest in the Harambee Trading Academy. Our
                team will review your submission and follow up to discuss your
                interests and available information.
              </p>
            </div>
          ) : (
            <>
              <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#2B2D2F] mb-1">
                Register Interest or Ask a Question
              </h3>
              <p className="text-[#6B6B6B] text-sm mb-7">
                All fields marked with * are required.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="AcademyApplyForm-components-first_name"
                      className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2"
                    >
                      First Name *
                    </label>
                    <input
                      id="AcademyApplyForm-components-first_name"
                      type="text"
                      name="first_name"
                      autoComplete="given-name"
                      required
                      placeholder="Your first name"
                      className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="AcademyApplyForm-components-last_name"
                      className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2"
                    >
                      Last Name *
                    </label>
                    <input
                      id="AcademyApplyForm-components-last_name"
                      type="text"
                      name="last_name"
                      autoComplete="family-name"
                      required
                      placeholder="Your last name"
                      className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="AcademyApplyForm-components-email"
                    className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2"
                  >
                    Email Address *
                  </label>
                  <input
                    id="AcademyApplyForm-components-email"
                    type="email"
                    name="email"
                    autoComplete="email"
                    required
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors"
                  />
                </div>

                <div>
                  <label
                    htmlFor="AcademyApplyForm-components-phone"
                    className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2"
                  >
                    Phone Number
                  </label>
                  <input
                    id="AcademyApplyForm-components-phone"
                    type="tel"
                    name="phone"
                    autoComplete="tel"
                    placeholder="Include your country code"
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors"
                  />
                </div>

                <div>
                  <label
                    htmlFor="AcademyApplyForm-components-country"
                    className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2"
                  >
                    Country of Residence *
                  </label>
                  <input
                    id="AcademyApplyForm-components-country"
                    type="text"
                    name="country"
                    autoComplete="country-name"
                    required
                    placeholder="e.g. United States, Kenya, United Kingdom"
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors"
                  />
                </div>

                <div>
                  <label
                    htmlFor="AcademyApplyForm-components-learning_format"
                    className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2"
                  >
                    Preferred Learning Format (Planned) *
                  </label>
                  <select
                    id="AcademyApplyForm-components-learning_format"
                    name="learning_format"
                    required
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] focus:outline-none focus:border-[#1A4D3C] transition-colors cursor-pointer"
                  >
                    <option value="">Select a format</option>
                    <option value="In-Person (Nairobi)">
                      In-Person (Nairobi)
                    </option>
                    <option value="Online">Online</option>
                    <option value="Both (In-Person & Online)">
                      Both (In-Person &amp; Online)
                    </option>
                    <option value="Not Sure Yet">Not Sure Yet</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="AcademyApplyForm-components-experience_level"
                    className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2"
                  >
                    Prior Trading Experience
                  </label>
                  <select
                    id="AcademyApplyForm-components-experience_level"
                    name="experience_level"
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] focus:outline-none focus:border-[#1A4D3C] transition-colors cursor-pointer"
                  >
                    <option value="">Select your level</option>
                    <option value="No experience">
                      No experience &mdash; complete beginner
                    </option>
                    <option value="Some exposure">
                      Some exposure, no formal training
                    </option>
                    <option value="Self-taught">
                      Self-taught with some practice
                    </option>
                    <option value="Experienced">
                      Experienced, seeking structured education
                    </option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="AcademyApplyForm-components-goals"
                    className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2"
                  >
                    Tell Us About Your Goals
                    <span className="ml-2 text-[#6B6B6B] font-normal normal-case tracking-normal">
                      ({charCount}/500)
                    </span>
                  </label>
                  <textarea
                    id="AcademyApplyForm-components-goals"
                    name="goals"
                    rows={4}
                    maxLength={500}
                    placeholder="What are you hoping to achieve through the Harambee Trading Academy? What would you like to learn?"
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors resize-none"
                  />
                </div>

                <div>
                  <label
                    htmlFor="AcademyApplyForm-components-referral_source"
                    className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2"
                  >
                    How Did You Hear About Us?
                  </label>
                  <select
                    id="AcademyApplyForm-components-referral_source"
                    name="referral_source"
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] focus:outline-none focus:border-[#1A4D3C] transition-colors cursor-pointer"
                  >
                    <option value="">Select an option</option>
                    <option value="Social Media">Social Media</option>
                    <option value="Word of Mouth">
                      Word of Mouth / Referral
                    </option>
                    <option value="Search Engine">Search Engine</option>
                    <option value="Community Event">Community Event</option>
                    <option value="Partner Organization">
                      Partner Organization
                    </option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-4 rounded-md bg-[#1A4D3C] text-white font-semibold text-sm hover:bg-[#0D2E1E] transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                >
                  {submitting ? "Submitting..." : "Register Interest"}
                </button>
                {error && (
                  <p
                    className="text-sm text-red-700 bg-red-50 rounded-md p-3 text-center"
                    role="alert"
                  >
                    {error}
                  </p>
                )}

                <p className="text-[#6B6B6B] text-xs text-center leading-relaxed">
                  By submitting this form, you agree to be contacted by the
                  Harambee Trading Academy team. Your details will be sent to
                  our team to respond to your inquiry. Please do not include
                  sensitive financial information.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
